// ===========================================
// Teacher: one Meet & Greet (#255, enquiries Phase 4)
// ===========================================
// `/:slug/teacher/meet-and-greets/:id`, the page the assignment and reminder emails link to.
// Shows what the teacher needs (child, instrument, time, the family's notes, the enquiry's original
// message, contact 1's name and phone) and the notes form. The server's narrow view carries no email,
// emergency contact, contact 2 or token, so there is nothing here to show. Notes are append-only.

import { useState, type ReactNode } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { Alert, Box, Button, Chip, Link, Paper, Skeleton, Stack, Typography } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import PageHeader from '../../components/common/PageHeader';
import MeetAndGreetNotesList, { NotesMissingChip } from '../../components/meetAndGreet/MeetAndGreetNotesList';
import RecordNotesDialog from '../../components/meetAndGreet/RecordNotesDialog';
import { useSchoolTimezone, useTenant } from '../../contexts/TenantContext';
import { useTeacherMeetAndGreet } from '../../hooks/useMeetAndGreetNotes';
import { formatMeetingTime } from '../../utils/meetAndGreetNotes';
import { telHref } from '../../components/admin/enquiries/enquiryUi';

function Section({ title, children, testId }: { title: string; children: ReactNode; testId?: string }) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid={testId}>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        {title}
      </Typography>
      {children}
    </Paper>
  );
}

export default function TeacherMeetAndGreetPage() {
  const { id } = useParams<{ id: string }>();
  const { schoolSlug } = useTenant();
  const timeZone = useSchoolTimezone();
  const { data: view, isLoading, isError, error } = useTeacherMeetAndGreet(id);
  const [recordOpen, setRecordOpen] = useState(false);

  const listPath = `/${schoolSlug}/teacher/meet-and-greets`;
  const back = (
    <Button component={RouterLink} to={listPath} startIcon={<ArrowBackIcon />} size="small" sx={{ mb: 1 }}>
      All my Meet & Greets
    </Button>
  );

  if (isLoading) {
    return (
      <Box data-testid="mng-teacher-loading">
        {back}
        <Stack spacing={2}>
          <Skeleton variant="text" width="40%" height={40} />
          <Skeleton variant="rectangular" height={100} />
          <Skeleton variant="rectangular" height={140} />
        </Stack>
      </Box>
    );
  }

  if (isError || !view) {
    const status = (error as { response?: { status?: number } } | null)?.response?.status;
    return (
      <Box>
        {back}
        <Alert severity="error">
          {status === 404
            ? 'This Meet & Greet was not found, or it is not assigned to you.'
            : 'Failed to load this Meet & Greet. Please try again.'}
        </Alert>
      </Box>
    );
  }

  const childName = `${view.studentFirstName} ${view.studentLastName}`.trim();
  const notes = Array.isArray(view.notes) ? view.notes : [];

  return (
    <Box>
      {back}
      <PageHeader title={`Meet & Greet: ${childName}`} subtitle={formatMeetingTime(view.scheduledDateTime, timeZone)} />

      <Stack spacing={2} sx={{ maxWidth: 800 }}>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
          {view.notesMissing && <NotesMissingChip size="medium" />}
          {view.completedAt && <Chip label="Completed" size="small" />}
          {view.canRecordNotes && (
            <Button variant="contained" onClick={() => setRecordOpen(true)}>
              Record notes
            </Button>
          )}
        </Stack>

        {view.status === 'PENDING_VERIFICATION' && (
          <Alert severity="info" data-testid="mng-teacher-waiting-family">
            Waiting for the family to confirm. You can see this Meet &amp; Greet now; notes can be recorded
            once it has been confirmed and has taken place.
          </Alert>
        )}

        {view.status === 'WAITLISTED' && (
          <Alert severity="info" data-testid="mng-teacher-waitlisted">
            On the waitlist. The family is waiting for a class place
            {view.canRecordNotes ? '; you can still record your notes on this meeting.' : '.'}
          </Alert>
        )}

        {!view.canRecordNotes && view.status !== 'PENDING_VERIFICATION' && (
          <Alert severity="info">
            Notes can be recorded once it has a time and has taken place.
          </Alert>
        )}

        <Section title="Child" testId="mng-teacher-child">
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {childName}
          </Typography>
          <Typography variant="body2">Age {view.studentAge}</Typography>
          <Typography variant="body2">Instrument: {view.instrument?.name ?? 'Not specified'}</Typography>
        </Section>

        <Section title="Parent or guardian" testId="mng-teacher-contact">
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {view.contactName}
          </Typography>
          {view.contactPhone && (
            <Link href={telHref(view.contactPhone)} variant="body2">
              {view.contactPhone}
            </Link>
          )}
        </Section>

        {view.additionalNotes && (
          <Section title="Notes from the family" testId="mng-teacher-family-notes">
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {view.additionalNotes}
            </Typography>
          </Section>
        )}

        {view.enquiryMessage && (
          <Section title="Their original enquiry" testId="mng-teacher-enquiry-message">
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {view.enquiryMessage}
            </Typography>
          </Section>
        )}

        <Section title="Recorded notes" testId="mng-teacher-notes">
          {notes.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No notes recorded yet.
            </Typography>
          ) : (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Notes cannot be edited. To correct something, record a new note.
              </Typography>
              <MeetAndGreetNotesList notes={notes} />
            </>
          )}
        </Section>
      </Stack>

      {view.canRecordNotes && id && (
        <RecordNotesDialog
          open={recordOpen}
          onClose={() => setRecordOpen(false)}
          meetAndGreetId={id}
          mode="teacher"
          childName={childName}
        />
      )}
    </Box>
  );
}
